/**
 * components/client/DatePickerField.tsx
 *
 * Custom 3-select date picker (day / month / year) for the client form.
 * Replaces the native <input type="date"> which renders broken/greyed-out on iOS.
 * Outputs and reads values in ISO format (YYYY-MM-DD).
 *
 * Updated Jun 2026: Uses internal local state for each select so that partial
 *   selections (e.g. only day picked) remain visible while the parent only receives
 *   a complete ISO date string or empty string.
 * Updated Jun 2026: Added aria-label + data-date-part on each select for
 *   accessibility. Added optional minYear prop (defaults to currentYear - 150).
 */

import { useState, useEffect } from 'react';
import { ChevronDown } from 'lucide-react';

const MONTHS = [
  'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
  'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre',
];

function daysInMonth(month: number, year: number): number {
  if (month === 0) return 31;
  return new Date(year, month, 0).getDate();
}

function parseIso(value: string): { day: number; month: number; year: number } {
  if (value && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [y, m, d] = value.split('-').map(Number);
    return { day: d, month: m, year: y };
  }
  return { day: 0, month: 0, year: 0 };
}

interface DatePickerFieldProps {
  value: string;
  onChange: (value: string) => void;
  minYear?: number;
}

export default function DatePickerField({ value, onChange, minYear }: DatePickerFieldProps) {
  const currentYear = new Date().getFullYear();
  const yearMin = minYear ?? (currentYear - 150);

  const parsed = parseIso(value);
  const [localDay, setLocalDay] = useState(parsed.day);
  const [localMonth, setLocalMonth] = useState(parsed.month);
  const [localYear, setLocalYear] = useState(parsed.year);

  // Sync from external value (e.g. loading a saved response on mount)
  useEffect(() => {
    const p = parseIso(value);
    setLocalDay(p.day);
    setLocalMonth(p.month);
    setLocalYear(p.year);
  }, [value]);

  // Emit ISO string upward only when all three parts are set
  useEffect(() => {
    if (localDay > 0 && localMonth > 0 && localYear > 0) {
      const d = String(localDay).padStart(2, '0');
      const m = String(localMonth).padStart(2, '0');
      onChange(`${localYear}-${m}-${d}`);
    } else {
      onChange('');
    }
  // onChange identity changes every render in the parent — exclude it from deps
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localDay, localMonth, localYear]);

  const maxDays = daysInMonth(localMonth, localYear || currentYear);

  const handleDay = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setLocalDay(parseInt(e.target.value, 10) || 0);
  };

  const handleMonth = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const month = parseInt(e.target.value, 10) || 0;
    setLocalMonth(month);
    if (localDay > 0 && month > 0) {
      const max = daysInMonth(month, localYear || currentYear);
      if (localDay > max) setLocalDay(max);
    }
  };

  const handleYear = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const year = parseInt(e.target.value, 10) || 0;
    setLocalYear(year);
    if (localDay > 0 && localMonth > 0 && year > 0) {
      const max = daysInMonth(localMonth, year);
      if (localDay > max) setLocalDay(max);
    }
  };

  const selectClass =
    'w-full appearance-none bg-white border border-neutral-200 rounded-xl px-4 py-3.5 pr-10 text-base text-neutral-900 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-400 transition-colors cursor-pointer';

  const years: number[] = [];
  for (let y = yearMin; y <= currentYear + 5; y++) years.push(y);

  return (
    <div className="grid grid-cols-3 gap-3">
      {/* Day */}
      <div className="relative">
        <select
          value={localDay || ''}
          onChange={handleDay}
          className={selectClass}
          aria-label="Jour"
          data-date-part="day"
        >
          <option value="">Jour</option>
          {Array.from({ length: maxDays }, (_, i) => i + 1).map((d) => (
            <option key={d} value={d}>{d}</option>
          ))}
        </select>
        <ChevronDown className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
      </div>

      {/* Month */}
      <div className="relative">
        <select
          value={localMonth || ''}
          onChange={handleMonth}
          className={selectClass}
          aria-label="Mois"
          data-date-part="month"
        >
          <option value="">Mois</option>
          {MONTHS.map((name, i) => (
            <option key={i + 1} value={i + 1}>{name}</option>
          ))}
        </select>
        <ChevronDown className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
      </div>

      {/* Year */}
      <div className="relative">
        <select
          value={localYear || ''}
          onChange={handleYear}
          className={selectClass}
          aria-label="Année"
          data-date-part="year"
        >
          <option value="">Année</option>
          {years.slice().reverse().map((y) => (
            <option key={y} value={y}>{y}</option>
          ))}
        </select>
        <ChevronDown className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
      </div>
    </div>
  );
}
